import { useTranslation } from "react-i18next";
import { Divider, Form, Icon, Segment, Message, Button } from "semantic-ui-react";
import { sdiClickDomain } from "utils/domains";
import RichText from "components/lib/Editors/RichText";
import { DateTimeInput } from "semantic-ui-calendar-react";
import { useSelector } from "react-redux";
import moment from "moment";

const linkRegex = /<a[^>]+href="([^"]+)"[^>]+>([^<]+)<\/a>/gi;

type IAnnouncement = {
  subject?: string;
  replyTo?: string;
  callToActionText?: string;
  callToActionLink?: string;
  body?: string;
  text?: string;
  calendarEvent?: {
    startDate?: string;
    endDate?: string;
    name?: string;
    location?: string;
    description?: string;
  };
};

type AnnouncementBaseProps = {
  pre?: React.ReactNode;
  post?: React.ReactNode;
  announcement: IAnnouncement;
  setAnnouncement: (announcement: any) => void;
};

const AnnouncementBase = ({ pre, post, announcement, setAnnouncement }: AnnouncementBaseProps) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);

  const links = (announcement.body ?? "").matchAll(linkRegex);
  const problematicLinks = Array.from(links).filter((link) => {
    const [_, href, text] = link;
    return href.trim() !== text.trim();
  });
  const { calendarEvent } = announcement;
  return (
    <Form>
      {pre}
      <Segment>
        <h3>Content (required)</h3>
        This will be sent to qualifying users as an email.
        <Divider hidden />
        <Message info size="small">
          <h4>
            <Icon name="lightbulb outline" />
            {t("announcements.form.placeholders.title")}
          </h4>
          <p>{t("announcements.form.placeholders.info")}</p>
          <ul>
            <li>
              <code>
                <mark>{"{firstName}"}</mark>
              </code>{" "}
              {t("announcements.form.placeholders.firstName")}
            </li>
            <li>
              <code>
                <mark>{"{lastName}"}</mark>
              </code>{" "}
              {t("announcements.form.placeholders.lastName")}
            </li>
          </ul>
        </Message>
        <Form.Input
          label={t("announcements.form.subject")}
          placeholder={`${t("announcements.form.subject")}...`}
          value={announcement.subject}
          onChange={(e, { value }) => setAnnouncement((prevState) => ({ ...prevState, subject: value }))}
        />
        <Form.Field label={t("announcements.form.body")} style={{ margin: 0 }} />
        <RichText
          preset="simple"
          fullMentionLinks
          autoHideMenu={false}
          placeholder={t("announcements.form.message")}
          value={announcement.body}
          onChange={(value, text) => {
            setAnnouncement((prevState) => ({ ...prevState, body: value, text }));
          }}
        />
        {problematicLinks.length !== 0 ? (
          <Message error style={{ display: "block" }}>
            Your message includes a labelled hyperlink. This will not be "clickable" in direct messages. For
            announcements we recommend that the hyperlink text is the same as the URL that it points to.
            <br />
            <br />
            We suggest making the following changes:
            <ul>
              {problematicLinks.map((link, index) => (
                <li key={index}>
                  "{link[2]}" {" => "} {link[1]}
                </li>
              ))}
            </ul>
          </Message>
        ) : null}
      </Segment>
      <Segment>
        <h3>Configuration (optional)</h3>
        This section allows you to further customise the announcement if needed.
        <Divider hidden />
        <Form.Input
          label="Reply-To address"
          placeholder="example@address.com"
          width={8}
          value={announcement.replyTo}
          onChange={(e, { value }) => setAnnouncement((prevState) => ({ ...prevState, replyTo: value }))}
        />
        <Form.Group>
          <Form.Input
            label="Call-To-Action"
            placeholder="View on SimplyDo"
            width={8}
            value={announcement.callToActionText}
            onChange={(e, { value }) => setAnnouncement((prevState) => ({ ...prevState, callToActionText: value }))}
          />
          <Form.Input
            label="CTA URL"
            placeholder={`${sdiClickDomain}/linkToChallenge`}
            width={8}
            value={announcement.callToActionLink}
            onChange={(e, { value }) => setAnnouncement((prevState) => ({ ...prevState, callToActionLink: value }))}
          />
        </Form.Group>
        <Form.Field>
          <Message info icon>
            <Message.Header>Call-To-Action preview:</Message.Header>
            <div style={{ margin: "10px 0 10px 20px", textAlign: "center" }}>
              <span
                style={{
                  background: "#14435b",
                  border: "10px solid #14435b",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: 5,
                  fontSize: 15,
                  boxShadow: "2px 2px 5px #888888",
                  fontFamily: "sans-serif",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {announcement.callToActionText || "View on SimplyDo"}
              </span>
            </div>
          </Message>
        </Form.Field>
        {post}
      </Segment>
      <Segment>
        <h4>Attach a calendar event</h4>
        <p>
          Create a calendar event that will be attached to emails sent with this announcement. This will enable
          recipients to add the event reminder to their own calendar.
        </p>
        <Button
          onClick={() => {
            if (calendarEvent) {
              setAnnouncement((prevState) => ({ ...prevState, calendarEvent: undefined }));
            } else {
              setAnnouncement((prevState) => ({ ...prevState, calendarEvent: {} }));
            }
          }}
        >
          {calendarEvent ? "Remove calendar event" : "Add calendar event"}
        </Button>
        {calendarEvent && (
          <>
            <Form widths="equal" style={{ marginTop: 10, marginBottom: 10 }}>
              <Form.Group>
                <DateTimeInput
                  label="Start date"
                  placeholder="Start date"
                  name="startDate"
                  value={calendarEvent?.startDate && moment(calendarEvent.startDate).format("DD-MM-YYYY HH:mm")}
                  iconPosition="left"
                  onChange={(e, { value }) => {
                    if (announcement?.calendarEvent?.endDate) {
                      setAnnouncement((prevState) => ({
                        ...prevState,
                        calendarEvent: {
                          ...prevState.calendarEvent,
                          startDate: moment(value, "DD-MM-YYYY HH:mm").toISOString(),
                        },
                      }));
                    } else {
                      setAnnouncement((prevState) => ({
                        ...prevState,
                        calendarEvent: {
                          ...prevState.calendarEvent,
                          startDate: moment(value, "DD-MM-YYYY HH:mm").toISOString(),
                          endDate: moment(value, "DD-MM-YYYY HH:mm").add(1, "hour").toISOString(),
                        },
                      }));
                    }
                  }}
                />
                <DateTimeInput
                  label="End date"
                  placeholder="End date"
                  name="endDate"
                  minDate={calendarEvent.startDate && moment(calendarEvent.startDate).format("DD-MM-YYYY HH:mm")}
                  value={calendarEvent.endDate && moment(calendarEvent.endDate).format("DD-MM-YYYY HH:mm")}
                  iconPosition="left"
                  onChange={(e, { value }) =>
                    setAnnouncement((prevState) => ({
                      ...prevState,
                      calendarEvent: {
                        ...prevState.calendarEvent,
                        endDate: moment(value, "DD-MM-YYYY HH:mm").toISOString(),
                      },
                    }))
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Input
                  label="Event name"
                  placeholder="Event name"
                  value={calendarEvent.name}
                  onChange={(e, { value }) =>
                    setAnnouncement((prevState) => ({
                      ...prevState,
                      calendarEvent: { ...prevState.calendarEvent, name: value },
                    }))
                  }
                />
                <Form.Input
                  label="Location"
                  placeholder="Location"
                  onChange={(e, { value }) =>
                    setAnnouncement((prevState) => ({
                      ...prevState,
                      calendarEvent: { ...prevState.calendarEvent, location: value },
                    }))
                  }
                  value={calendarEvent.location}
                />
              </Form.Group>
              <Form.TextArea
                label="Description"
                placeholder="Description"
                value={calendarEvent.description}
                onChange={(e, { value }) =>
                  setAnnouncement((prevState) => ({
                    ...prevState,
                    calendarEvent: { ...prevState.calendarEvent, description: value },
                  }))
                }
              />
            </Form>
            <p>{`The calendar invite will be displayed as originating from your email address ${user?.emails[0]?.emailAdddress}. Note that RSVPs may be received at this address.`}</p>
          </>
        )}
      </Segment>
    </Form>
  );
};

export default AnnouncementBase;
